/**
 * DiscussBotDialog.jsx — iter927 (Feb 2026)
 *
 * Replaces the previous "navigate to /dashboard AI chat" flow with an
 * inline modal that opens directly on top of the AutoDiagnosisPanel.
 * The specialist bot's pre-populated question is loaded into a textarea;
 * user can edit and hit "Питай" — response streams in below.
 *
 * Educational-only. Not a diagnostic tool.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Loader2, Send, Sparkles, X, Bot, Copy, Check } from "lucide-react";
import CreditBadge from "@/components/CreditBadge";  // iter1118
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useCreditGate } from "@/components/CreditGateContext";
import { toast } from "sonner";

const BOT_LABELS = {
  endobot:   { bg: "EndoBot",   en: "EndoBot"   },
  hemobot:   { bg: "HemoBot",   en: "HemoBot"   },
  nephrobot: { bg: "NephroBot", en: "NephroBot" },
  nutribot:  { bg: "NutriBot",  en: "NutriBot"  },
};

const LIKELIHOOD_LABELS = {
  low: { bg: "нисък", en: "low" },
  moderate: { bg: "среден", en: "moderate" },
  high: { bg: "висок", en: "high" },
};

// Markdown component overrides — defined outside the component to avoid
// remounting react-markdown nodes on every parent render (react-lint rule
// no-unstable-nested-components).
function buildMarkdownComponents(isDark) {
  const strongCls = `font-semibold ${isDark ? "text-white" : "text-gray-900"}`;
  const hCls = `text-sm font-bold mt-3 mb-1.5 ${isDark ? "text-white" : "text-gray-900"}`;
  const h3Cls = `text-xs font-bold mt-2.5 mb-1 ${isDark ? "text-white" : "text-gray-900"}`;
  const codeCls = `px-1 py-0.5 rounded text-[10px] font-mono ${isDark ? "bg-white/10 text-cyan-200" : "bg-gray-100 text-cyan-700"}`;
  const linkCls = `underline ${isDark ? "text-cyan-300" : "text-cyan-700"}`;
  return {
    p:      ({children}) => <p className="text-xs mb-2 leading-relaxed">{children}</p>,
    strong: ({children}) => <strong className={strongCls}>{children}</strong>,
    em:     ({children}) => <em className="italic">{children}</em>,
    h1:     ({children}) => <h4 className={hCls}>{children}</h4>,
    h2:     ({children}) => <h4 className={hCls}>{children}</h4>,
    h3:     ({children}) => <h5 className={h3Cls}>{children}</h5>,
    ul:     ({children}) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
    ol:     ({children}) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
    li:     ({children}) => <li className="text-xs">{children}</li>,
    code:   ({children}) => <code className={codeCls}>{children}</code>,
    a:      ({children, href}) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className={linkCls}>{children}</a>
    ),
  };
}

export default function DiscussBotDialog({ open, onOpenChange, insight, prefillQuery }) {
  const { API, refreshUser } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { requireCredits } = useCreditGate();
  const bg = language === "bg";

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");           // full text from backend
  const [visibleAnswer, setVisibleAnswer] = useState(""); // typewriter progress
  const [agentName, setAgentName] = useState("");
  const [copied, setCopied] = useState(false);
  const streamTimerRef = useRef(null);
  const answerScrollRef = useRef(null);

  // Load prefill when dialog opens with a new insight.
  useEffect(() => {
    if (open && prefillQuery) {
      setQuery(prefillQuery);
      setAnswer("");
      setVisibleAnswer("");
      setCopied(false);
    }
  }, [open, prefillQuery]);

  // Clear any pending stream timer on unmount / close
  useEffect(() => {
    return () => {
      if (streamTimerRef.current) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
      }
    };
  }, []);

  // Typewriter effect — reveals `answer` word-by-word (~40 chars per tick).
  // We keep the render responsive by batching characters, not per-character.
  useEffect(() => {
    if (!answer) return;
    // Reset any existing timer
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    setVisibleAnswer("");
    let idx = 0;
    const CHUNK = 12;
    const TICK_MS = 18;
    streamTimerRef.current = setInterval(() => {
      idx = Math.min(idx + CHUNK, answer.length);
      setVisibleAnswer(answer.slice(0, idx));
      // Auto-scroll answer container to bottom
      if (answerScrollRef.current) {
        answerScrollRef.current.scrollTop = answerScrollRef.current.scrollHeight;
      }
      if (idx >= answer.length) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
      }
    }, TICK_MS);
    return () => {
      if (streamTimerRef.current) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
      }
    };
  }, [answer]);

  const botKey = insight?.bot || "";
  const botLabel = insight?.bot_name || BOT_LABELS[botKey]?.[bg ? "bg" : "en"] || "AI";

  const send = useCallback(async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setAnswer("");
    setVisibleAnswer("");
    setCopied(false);
    try {
      // iter933 — Explicit 55s timeout so we fail cleanly instead of
      // letting Cloudflare return a 502 HTML page at 60s (which used to
      // break the app in production — see prod bug report from user).
      const { data } = await axios.post(
        `${API}/query/execute`,
        { query, language },
        { timeout: 55000 },
      );
      const text = data?.final_response?.summary || "";
      setAnswer(text || (bg ? "Няма отговор." : "No answer."));
      const primary = data?.final_response?.agent_responses?.[0];
      setAgentName(primary?.agent_label_bg || primary?.agent_name || "");
      await refreshUser();
    } catch (e) {
      if (e.response?.status === 402) {
        onOpenChange?.(false);
        requireCredits(bg ? "AI анализ" : "AI analysis", e.response?.data?.detail?.match(/Required: (\d+)/)?.[1] || 1);
        return;
      }
      // iter933 — Handle timeout / gateway errors gracefully.
      const isTimeout = e.code === "ECONNABORTED" || e.message?.includes("timeout");
      const isGateway = e.response?.status === 502 || e.response?.status === 504;
      if (isTimeout || isGateway) {
        setAnswer(bg
          ? "⏱️ Обработката на този въпрос отне повече от очакваното. Опитайте с по-кратък и по-конкретен въпрос (напр. за един маркер или един pattern) — така AI ще отговори по-бързо."
          : "⏱️ Processing this question took longer than expected. Try a shorter, more focused question (about one marker or one pattern) so the AI can respond faster.");
        toast.warning(bg ? "AI обработката отне повече време — опитайте по-кратък въпрос." : "AI processing timed out — try a shorter question.");
        return;
      }
      const msg = e.response?.data?.detail || (bg ? "Грешка при заявката." : "Request failed.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [query, loading, API, language, bg, refreshUser, onOpenChange, requireCredits]);

  const handleCopy = useCallback(async () => {
    if (!answer) return;
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error(bg ? "Копирането не успя." : "Copy failed.");
    }
  }, [answer, bg]);

  const T = bg ? {
    title: `Обсъди с ${botLabel}`,
    desc: "Питайте специалист-бота за клиничния модел, идентифициран от Вашия автоматичен анализ. Отговорите са образователни, НЕ диагностични.",
    placeholder: "Напишете Вашия въпрос…",
    send: "Питай",
    edu: "⚠️ Образователна информация. Обсъдете конкретни решения с Вашия лекар.",
    thinking: "Обмислям…",
    edit: "Редактирай",
    copy: "Копирай",
    copied: "Копирано",
  } : {
    title: `Discuss with ${botLabel}`,
    desc: "Ask the specialist bot about the pattern identified in your auto-diagnosis report. Answers are educational, NOT diagnostic.",
    placeholder: "Type your question…",
    send: "Ask",
    edu: "⚠️ Educational information only. Discuss clinical decisions with your physician.",
    thinking: "Thinking…",
    edit: "Edit",
    copy: "Copy",
    copied: "Copied",
  };

  const isStreaming = answer && visibleAnswer.length < answer.length;
  const mdComponents = React.useMemo(() => buildMarkdownComponents(isDark), [isDark]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-2xl max-w-[95vw] ${isDark ? "bg-[hsl(223,47%,10%)] border-white/10 text-white" : "bg-white"}`}
        data-testid="discuss-bot-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <span>{T.title}</span>
          </DialogTitle>
          <DialogDescription className={isDark ? "text-white/60" : "text-gray-500"}>
            {T.desc}
          </DialogDescription>
        </DialogHeader>

        {/* Insight context chip */}
        {insight?.name_bg && (
          <div className={`text-[11px] rounded-lg px-2.5 py-1.5 ${
            isDark ? "bg-cyan-500/10 text-cyan-200 border border-cyan-400/20"
                   : "bg-cyan-50 text-cyan-800 border border-cyan-200"
          }`}
          data-testid="discuss-bot-insight-chip">
            🩺 <span className="font-bold">{insight.name_bg}</span>
            {insight.likelihood && <span className="ml-1 opacity-70">· {insight.likelihood}</span>}
          </div>
        )}

        {/* Query textarea */}
        <div>
          <label className={`block text-[11px] font-semibold mb-1 ${isDark ? "text-white/70" : "text-gray-600"}`}>
            {T.edit}
          </label>
          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={6}
            placeholder={T.placeholder}
            data-testid="discuss-bot-query-input"
            className={`text-xs resize-none ${
              isDark ? "bg-[hsl(223,47%,14%)] border-white/10 text-white"
                     : "bg-gray-50 border-gray-200"
            }`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
          />
        </div>

        {/* Send button */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className={`text-[10px] italic ${isDark ? "text-amber-300/70" : "text-amber-700/80"}`}>
            {T.edu}
          </div>
          <div className="flex items-center gap-2">
            {/* iter1118 — индикатор за цена */}
            <CreditBadge featureId="ai_question" fallbackCost={1} />
            <Button
              onClick={send}
              disabled={loading || !query.trim()}
              data-testid="discuss-bot-send-btn"
              className="bg-gradient-to-r from-cyan-500 to-sky-500 hover:opacity-90 text-white h-9 px-4"
            >
              {loading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> {T.thinking}</>
              ) : (
                <><Send className="w-3.5 h-3.5 mr-1.5" /> {T.send}</>
              )}
            </Button>
          </div>
        </div>

        {/* Answer area */}
        {(loading || answer) && (
          <div
            ref={answerScrollRef}
            className={`mt-1 rounded-xl border p-3 max-h-[45vh] overflow-y-auto ${
              isDark ? "bg-[hsl(223,47%,14%)] border-white/10"
                     : "bg-gradient-to-b from-cyan-50/40 to-white border-cyan-200"
            }`}
            data-testid="discuss-bot-answer"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-1.5">
                <Sparkles className={`w-3.5 h-3.5 ${isDark ? "text-cyan-300" : "text-cyan-600"}`} />
                <span className={`text-[10px] font-bold uppercase tracking-wider ${
                  isDark ? "text-cyan-300" : "text-cyan-700"
                }`}>
                  {agentName || botLabel}
                </span>
                {isStreaming && (
                  <span className={`text-[9px] ${isDark ? "text-white/40" : "text-gray-400"} italic ml-1`}>
                    · {T.thinking}
                  </span>
                )}
              </div>
              {answer && !loading && (
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="discuss-bot-copy-btn"
                  className={`text-[10px] px-2 py-0.5 rounded-md flex items-center gap-1 transition-colors ${
                    isDark ? "bg-white/[0.06] hover:bg-white/[0.12] text-white/70"
                           : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                  }`}
                >
                  {copied ? <><Check className="w-3 h-3" /> {T.copied}</>
                          : <><Copy className="w-3 h-3" /> {T.copy}</>}
                </button>
              )}
            </div>
            {loading && !answer ? (
              <div className={`flex items-center gap-2 text-xs ${isDark ? "text-white/70" : "text-gray-500"}`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {T.thinking}
              </div>
            ) : (
              <div className={`text-xs leading-relaxed prose prose-sm max-w-none ${
                isDark ? "prose-invert text-white/90" : "text-gray-800"
              }`}>
                <ReactMarkdown components={mdComponents}>
                  {visibleAnswer}
                </ReactMarkdown>
                {isStreaming && (
                  <span
                    aria-hidden="true"
                    className={`inline-block w-1.5 h-3 ml-0.5 align-middle animate-pulse ${
                      isDark ? "bg-cyan-300" : "bg-cyan-600"
                    }`}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
